// utils/heicPhotos.js — an iPhone photo (HEIC) becomes a JPEG before anything
// else touches it.
//
// 2026-09-15. Brett, dragging three iPhone photos onto a pin card on the PC:
// "Drag and drop isnt working into droppin". The drop worked; the photos were
// HEIC, which Chrome cannot decode — the card showed three broken tiles,
// makePhotoBlobs could make no display copy or thumbnail, and the original went
// up as HEIC that no other browser could show. Every place a photo comes in now
// passes it through here first.
//
// HEIC is recognised by its HEADER, not its name or type: a file dragged from
// Windows often arrives with no type at all, and a renamed file lies. The
// converter is loaded only when a HEIC actually arrives.
//
// 2026-09-15 (later) — heic-to, not heic2any. Brett, after a day's photos went
// onto pins: "IMG_0325(2).HEIC, IMG_0326(1).HEIC … could not be read", then "i
// got out of the report and back in and it worked". Tested with his 22 files:
// every one converts in a fresh page with either library, so the photos were
// never the problem — the sitting was. heic2any runs a 2019 libheif on the
// page's own thread and never frees a decoded image (no free() anywhere in it;
// the page heap climbed 58 → 102 MB over the 22, ~9 s a photo with the page
// frozen), and was last published in 2023. heic-to decodes in a Web Worker and
// frees each image after decoding (the heap fell back between photos, ~6 s a
// photo, page still responsive). A conversion that still fails is tried once
// more, and the reason goes to the console instead of being thrown away.

import { makePhotoBlobs } from './imageVariants.js';

// ISO-BMFF brands that mean a HEIC / HEIF still image.
const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs']);
// Generic HEIF brands, shared with AVIF — HEIC only when a HEIC brand is listed too.
const HEIF_GENERIC = new Set(['mif1', 'msf1']);

const ascii = (bytes, from, len) => String.fromCharCode(...bytes.slice(from, from + len));

/** Do these leading bytes (at least 12) open a HEIC / HEIF image? Pure. */
export function isHeicHeader(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (b.length < 12 || ascii(b, 4, 4) !== 'ftyp') return false;
  const major = ascii(b, 8, 4);
  if (HEIC_BRANDS.has(major)) return true;
  if (!HEIF_GENERIC.has(major)) return false;
  // compatible brands follow the minor version (offset 16), four bytes each
  const boxSize = Math.min(b.length, ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0 || b.length);
  for (let o = 16; o + 4 <= boxSize; o += 4) if (HEIC_BRANDS.has(ascii(b, o, 4))) return true;
  return false;
}

/** A name or type that says HEIC — a fast path before reading any bytes. Pure. */
export function saysHeic(file) {
  return !!file && (/^image\/hei[cf]/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || ''));
}

export async function isHeicFile(file) {
  if (!file) return false;
  if (saysHeic(file)) return true;
  if (file.type && !/^image\//i.test(file.type) && file.type !== 'application/octet-stream') return false;
  try { return isHeicHeader(new Uint8Array(await file.slice(0, 64).arrayBuffer())); } catch (_e) { return false; }
}

/** Could this file be a photo? Types lie from Windows, so an empty type or a HEIC name counts. Pure. */
export function mayBePhoto(file) {
  return !!file && (!file.type || /^image\//i.test(file.type) || file.type === 'application/octet-stream' || saysHeic(file));
}

/**
 * The file a browser can show: a HEIC converted to JPEG (named .jpg, its date
 * kept), anything else as it came. Throws when a HEIC cannot be converted.
 */
export async function toBrowserImage(file) {
  if (!(await isHeicFile(file))) return file;
  let blob = null;
  let lastError = null;
  for (let attempt = 1; attempt <= 2 && !blob; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const { heicTo } = await import('heic-to');
      // eslint-disable-next-line no-await-in-loop
      // v0.194.0 — 0.9 → 0.95. This JPEG is not the end of the line: it is
      // handed to makePhotoBlobs, so every loss here is baked into the stored
      // original. With ORIGINAL_MAX_EDGE now above a phone frame, makePhotoBlobs
      // re-encodes nothing and THIS is the only lossy step an iPhone photo takes.
      // Worth the extra few hundred KB to make it the good one.
      blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.95 });
    } catch (e) {
      lastError = e;
      console.warn(`[heic] ${file.name || 'photo'}: conversion attempt ${attempt} failed:`, e);
    }
  }
  if (!blob) {
    throw Object.assign(new Error(`${file.name || 'That photo'} is an iPhone (HEIC) photo that could not be converted`), { cause: lastError, heic: true });
  }
  const name = String(file.name || 'photo').replace(/\.(heic|heif)$/i, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
}

/** Many files, one at a time: the converted ones, and the names that failed. */
export async function toBrowserImages(fileList) {
  const files = []; const failed = [];
  for (const f of Array.from(fileList || [])) {
    try {
      // eslint-disable-next-line no-await-in-loop
      files.push(await toBrowserImage(f));
    } catch (e) {
      failed.push(f && f.name ? f.name : 'a photo');
    }
  }
  return { files, failed };
}

/**
 * For a screen that sends the photo as a data URL: a HEIC is converted AND
 * brought down to the stored-original size (a full-resolution JPEG of a phone
 * photo is several MB); anything else is returned as it came.
 */
export async function toUploadableImage(file) {
  const jpeg = await toBrowserImage(file);
  if (jpeg === file) return file;
  const { original } = await makePhotoBlobs(jpeg);
  if (!original || original === jpeg) return jpeg;
  return new File([original], jpeg.name, { type: original.type || 'image/jpeg', lastModified: jpeg.lastModified });
}
